import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { API } from 'aws-amplify';
import { v4 as uuidv4 } from 'uuid';
import { Loader2, Plus, Trash2, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { useToast } from '../../../components/ui/toast';
import { cn } from '../../../lib/utils';

// ── Types ───────────────────────────────────────────────────

interface Schedule {
  id?: string;
  days: number[];
  startTime: number;
  endTime: number;
  minimumMeetingLength: number;
  maximumMeetingLength: number;
  defaultMeetingLength: number;
  minutesBetweenMeetings: number;
}

interface AggregatedSchedule extends Schedule {
  slots: Schedule[];
}

interface WorkingHoursModalProps {
  open: boolean;
  onClose: () => void;
  userID: string;
}

// ── GraphQL ─────────────────────────────────────────────────

const maintainPersonDefaultSchedule = `
  mutation MaintainPersonDefaultSchedule($request: AWSJSON!) {
    maintainPersonDefaultSchedule(request: $request)
  }
`;

const listPersonDefaultScheduleByUser = `
  query ListPersonDefaultScheduleByUser($userID: ID!) {
    listPersonDefaultScheduleByUser(userID: $userID) {
      items {
        days
        defaultMeetingLength
        endDate
        endTime
        maximumMeetingLength
        minimumMeetingLength
        minutesBetweenMeetings
        startTime
      }
    }
  }
`;

// ── Helpers ─────────────────────────────────────────────────

declare const global: {
  me: { id: string; orgID: string };
  appSettings?: {
    defaultSchedule?: {
      default?: Array<{
        days?: number[];
        defaultMeetingLength?: number;
        endTime?: number;
        maximumMeetingLength?: number;
        minimumMeetingLength?: number;
        minutesBetweenMeetings?: number;
        startTime?: number;
      }>;
    };
  };
};

function getDefaultScheduleConfig() {
  const globalSettings = global?.appSettings?.defaultSchedule?.default?.[0];
  if (globalSettings) {
    return {
      days: globalSettings.days || [1, 2, 3, 4, 5],
      defaultMeetingLength: globalSettings.defaultMeetingLength || 90,
      endTime: globalSettings.endTime || 1260,
      maximumMeetingLength: globalSettings.maximumMeetingLength || 600,
      minimumMeetingLength: globalSettings.minimumMeetingLength || 30,
      minutesBetweenMeetings: globalSettings.minutesBetweenMeetings || 0,
      startTime: globalSettings.startTime || 720,
    };
  }
  return {
    days: [1, 2, 3, 4, 5],
    defaultMeetingLength: 90,
    endTime: 1260,
    maximumMeetingLength: 600,
    minimumMeetingLength: 30,
    minutesBetweenMeetings: 0,
    startTime: 720,
  };
}

function formatTime(minutes: number): string {
  if (minutes < 0 || isNaN(minutes)) {
    return '--:--';
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${displayHours}:${mins.toString().padStart(2, '0')} ${period}`;
}

function generateTimeOptions(): { value: number; label: string }[] {
  const options: { value: number; label: string }[] = [];
  for (let minutes = 0; minutes < 1440; minutes += 15) {
    options.push({ value: minutes, label: formatTime(minutes) });
  }
  return options;
}

function generateDurationOptions(): { value: number; label: string }[] {
  const globalSettings = global?.appSettings?.defaultSchedule?.default?.[0];
  const maxMeetingLength = Math.max(
    globalSettings?.maximumMeetingLength || 300,
    300
  );
  const options: { value: number; label: string }[] = [];
  for (let duration = 15; duration <= maxMeetingLength; duration += 15) {
    options.push({ value: duration, label: `${duration} min` });
  }
  return options;
}

// ── Validation ──────────────────────────────────────────────

function validateSchedules(schedules: AggregatedSchedule[]): string | null {
  if (!schedules.length) {
    return "Schedule can't be empty";
  }

  const hasTimeSlots = schedules.some((sched) => sched.slots.length > 0);
  if (!hasTimeSlots) {
    return 'Please add at least one time slot';
  }

  for (const sched of schedules) {
    for (const slot of sched.slots) {
      if (
        slot.startTime < 0 ||
        slot.endTime < 0 ||
        slot.startTime >= 1440 ||
        slot.endTime > 1440 ||
        slot.startTime >= slot.endTime
      ) {
        return `Invalid time: ${
          slot.startTime >= 0 ? formatTime(slot.startTime) : 'Invalid start'
        } - ${slot.endTime >= 0 ? formatTime(slot.endTime) : 'Invalid end'}`;
      }

      const slotDuration = slot.endTime - slot.startTime;
      if (slotDuration < 30) {
        return `Time slot ${formatTime(slot.startTime)}-${formatTime(
          slot.endTime
        )} is too short (minimum 30 minutes)`;
      }
      if (slotDuration > 16 * 60) {
        return `Time slot ${formatTime(slot.startTime)}-${formatTime(
          slot.endTime
        )} is too long (maximum 16 hours)`;
      }
      if (slot.defaultMeetingLength > slotDuration) {
        return `Meeting duration (${
          slot.defaultMeetingLength
        } min) exceeds slot ${formatTime(slot.startTime)}-${formatTime(
          slot.endTime
        )}`;
      }
    }

    // Check overlapping slots
    for (let i = 0; i < sched.slots.length; i++) {
      for (let j = i + 1; j < sched.slots.length; j++) {
        const slot1 = sched.slots[i];
        const slot2 = sched.slots[j];
        if (
          slot1.startTime < slot2.endTime &&
          slot2.startTime < slot1.endTime
        ) {
          return `Time slots overlap: ${formatTime(
            slot1.startTime
          )}-${formatTime(slot1.endTime)} and ${formatTime(
            slot2.startTime
          )}-${formatTime(slot2.endTime)}`;
        }
      }
    }
  }

  return null;
}

// ── Sub-components ──────────────────────────────────────────

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function DaySelector({
  selectedDays,
  onToggle,
}: {
  selectedDays: number[];
  onToggle: (days: number[]) => void;
}) {
  const handleDayToggle = (day: number) => {
    if (selectedDays.includes(day)) {
      const newDays = selectedDays.filter((d) => d !== day);
      if (newDays.length > 0) {
        onToggle(newDays);
      }
    } else {
      onToggle([...selectedDays, day].sort());
    }
  };

  return (
    <div className="mb-4">
      <p className="text-sm text-muted-foreground mb-2">Days available</p>
      <div className="flex gap-2 flex-wrap">
        {[0, 1, 2, 3, 4, 5, 6].map((day) => {
          const isSelected = selectedDays.includes(day);
          return (
            <button
              key={day}
              type="button"
              onClick={() => handleDayToggle(day)}
              className={cn(
                'h-9 px-3.5 rounded-full text-sm font-medium border transition-colors',
                isSelected
                  ? 'bg-foreground text-background border-foreground'
                  : 'bg-background text-foreground border-border hover:bg-muted'
              )}
            >
              {DAY_NAMES[day]}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const TIME_OPTIONS = generateTimeOptions();

function TimeSlotRow({
  slot,
  onChangeStart,
  onChangeEnd,
  onChangeDuration,
  onAdd,
  onRemove,
}: {
  slot: Schedule;
  onChangeStart: (value: number) => void;
  onChangeEnd: (value: number) => void;
  onChangeDuration: (value: number) => void;
  onAdd: () => void;
  onRemove: () => void;
}) {
  const durationOptions = React.useMemo(() => generateDurationOptions(), []);

  return (
    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl mb-2 flex-wrap">
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground min-w-[36px]">From</span>
        <Select
          value={slot.startTime >= 0 ? slot.startTime.toString() : ''}
          onValueChange={(val) => onChangeStart(parseInt(val))}
        >
          <SelectTrigger className="w-[120px] h-9 rounded-lg bg-background">
            <SelectValue placeholder="--:--" />
          </SelectTrigger>
          <SelectContent className="max-h-[200px]">
            {TIME_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value.toString()}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground min-w-[32px]">
          Until
        </span>
        <Select
          value={slot.endTime >= 0 ? slot.endTime.toString() : ''}
          onValueChange={(val) => onChangeEnd(parseInt(val))}
        >
          <SelectTrigger className="w-[120px] h-9 rounded-lg bg-background">
            <SelectValue placeholder="--:--" />
          </SelectTrigger>
          <SelectContent className="max-h-[200px]">
            {TIME_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value.toString()}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground min-w-[28px]">Slot</span>
        <Select
          value={slot.defaultMeetingLength.toString()}
          onValueChange={(val) => onChangeDuration(parseInt(val))}
        >
          <SelectTrigger className="w-[110px] h-9 rounded-lg bg-background">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-[200px]">
            {durationOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value.toString()}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-1.5 ml-auto">
        <button
          type="button"
          onClick={onAdd}
          className="size-8 flex items-center justify-center rounded-lg border border-border hover:bg-muted transition-colors"
        >
          <Plus className="size-4 text-foreground" />
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="size-8 flex items-center justify-center rounded-lg border border-border hover:bg-destructive/10 hover:border-destructive/50 hover:text-destructive transition-colors"
        >
          <Trash2 className="size-4" />
        </button>
      </div>
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────

export function WorkingHoursModal({
  open,
  onClose,
  userID,
}: WorkingHoursModalProps) {
  const toast = useToast();
  const [aggregatedSchedules, setAggregatedSchedules] = useState<
    AggregatedSchedule[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // ── Fetch schedule ──────────────────────────────────────

  const fetchSchedule = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = (await API.graphql({
        query: listPersonDefaultScheduleByUser,
        variables: { userID },
      })) as {
        data: {
          listPersonDefaultScheduleByUser: { items: any[] };
        };
      };

      const items = res.data.listPersonDefaultScheduleByUser.items;
      let schedule: any[];

      if (items.length > 0) {
        schedule = items.map((item: any) => ({
          ...item,
          // Convert Python days (0=Monday, 6=Sunday) to JS days (0=Sunday, 6=Saturday)
          days: item.days.map((day: number) => (day === 6 ? 0 : day + 1)),
        }));
      } else {
        schedule = [getDefaultScheduleConfig()];
      }

      const aggregated = aggregateSchedules(schedule);
      setAggregatedSchedules(aggregated);
    } catch (error) {
      console.error('Error fetching schedule:', error);
      const aggregated = aggregateSchedules([getDefaultScheduleConfig()]);
      setAggregatedSchedules(aggregated);
    } finally {
      setIsLoading(false);
    }
  }, [userID]);

  useEffect(() => {
    if (open && userID) {
      fetchSchedule();
    }
  }, [open, userID, fetchSchedule]);

  // ── Aggregation logic ───────────────────────────────────

  function aggregateSchedules(rawSchedules: any[]): AggregatedSchedule[] {
    if (!rawSchedules.length) {
      return [];
    }

    // Group by same time configuration, then reduce by same days
    const grouped = rawSchedules.reduce((result: any, obj: any) => {
      const key = JSON.stringify({
        startTime: obj.startTime,
        endTime: obj.endTime,
        minimumMeetingLength: obj.minimumMeetingLength,
        maximumMeetingLength: obj.maximumMeetingLength,
        defaultMeetingLength: obj.defaultMeetingLength,
        minutesBetweenMeetings: obj.minutesBetweenMeetings,
      });
      if (!result[key]) {
        result[key] = {
          days: [...obj.days],
          slots: [{ ...obj, id: uuidv4() }],
        };
      } else {
        result[key].days.push(...obj.days);
      }
      return result;
    }, {});

    // Reduce by same days to combine multiple time slots
    const reducedSchedules = Object.values(grouped).reduce(
      (result: Record<string, AggregatedSchedule>, schedule: any) => {
        const key = JSON.stringify(
          Array.from(new Set(schedule.days as number[])).sort()
        );
        if (!result[key]) {
          result[key] = {
            days: Array.from(new Set(schedule.days as number[])).sort(),
            slots: [{ ...schedule.slots[0], id: uuidv4() }],
            startTime: schedule.slots[0]?.startTime || 0,
            endTime: schedule.slots[0]?.endTime || 0,
            minimumMeetingLength: schedule.slots[0]?.minimumMeetingLength || 30,
            maximumMeetingLength:
              schedule.slots[0]?.maximumMeetingLength || 600,
            defaultMeetingLength: schedule.slots[0]?.defaultMeetingLength || 90,
            minutesBetweenMeetings:
              schedule.slots[0]?.minutesBetweenMeetings || 0,
          };
        } else {
          result[key].slots.push({ ...schedule.slots[0], id: uuidv4() });
        }
        return result;
      },
      {} as Record<string, AggregatedSchedule>
    );

    return Object.values(reducedSchedules);
  }

  // ── Schedule mutations ──────────────────────────────────

  const updateScheduleDays = (schedIndex: number, selectedDays: number[]) => {
    if (selectedDays.length === 0) {
      return;
    }
    setAggregatedSchedules((prev) => {
      const updated = [...prev];
      updated[schedIndex] = {
        ...updated[schedIndex],
        days: selectedDays,
        slots: updated[schedIndex].slots.map((slot) => ({
          ...slot,
          days: selectedDays,
        })),
      };
      return updated;
    });
  };

  const updateSlotStartTime = (
    schedIndex: number,
    slotId: string,
    value: number
  ) => {
    setAggregatedSchedules((prev) =>
      prev.map((sched, i) => {
        if (i !== schedIndex) {
          return sched;
        }
        return {
          ...sched,
          slots: sched.slots.map((slot) => {
            if (slot.id !== slotId) {
              return slot;
            }
            if (slot.endTime > 0 && value >= slot.endTime) {
              toast.error('Start time must be before end time');
              return slot;
            }
            return { ...slot, startTime: value };
          }),
        };
      })
    );
  };

  const updateSlotEndTime = (
    schedIndex: number,
    slotId: string,
    value: number
  ) => {
    setAggregatedSchedules((prev) =>
      prev.map((sched, i) => {
        if (i !== schedIndex) {
          return sched;
        }
        return {
          ...sched,
          slots: sched.slots.map((slot) => {
            if (slot.id !== slotId) {
              return slot;
            }
            if (slot.startTime >= 0 && value <= slot.startTime) {
              toast.error('End time must be after start time');
              return slot;
            }
            return { ...slot, endTime: value };
          }),
        };
      })
    );
  };

  const updateSlotDuration = (
    schedIndex: number,
    slotId: string,
    value: number
  ) => {
    setAggregatedSchedules((prev) =>
      prev.map((sched, i) => {
        if (i !== schedIndex) {
          return sched;
        }
        return {
          ...sched,
          slots: sched.slots.map((slot) =>
            slot.id === slotId ? { ...slot, defaultMeetingLength: value } : slot
          ),
        };
      })
    );
  };

  const addSlot = (schedIndex: number) => {
    const defaultConfig = getDefaultScheduleConfig();
    const sched = aggregatedSchedules[schedIndex];
    const newSlot: Schedule = {
      id: uuidv4(),
      days: sched.days,
      defaultMeetingLength: defaultConfig.defaultMeetingLength,
      endTime: -1,
      maximumMeetingLength: defaultConfig.maximumMeetingLength,
      minimumMeetingLength: defaultConfig.minimumMeetingLength,
      minutesBetweenMeetings: defaultConfig.minutesBetweenMeetings,
      startTime: -1,
    };
    setAggregatedSchedules((prev) => {
      const updated = [...prev];
      updated[schedIndex] = {
        ...sched,
        slots: [...sched.slots, newSlot],
      };
      return updated;
    });
  };

  const removeSlot = (slotId: string) => {
    setAggregatedSchedules((prev) =>
      prev
        .map((sched) => ({
          ...sched,
          slots: sched.slots.filter((slot) => slot.id !== slotId),
        }))
        .filter((sched) => sched.slots.length > 0)
    );
  };

  const addAnotherDay = () => {
    const allDays = [0, 1, 2, 3, 4, 5, 6];
    const existingDays = aggregatedSchedules.reduce(
      (days: number[], sched) => [...days, ...sched.days],
      []
    );
    const uniqueExisting = Array.from(new Set(existingDays));
    const missingDays = allDays.filter((d) => !uniqueExisting.includes(d));

    if (missingDays.length === 0) {
      return;
    }

    const defaultConfig = getDefaultScheduleConfig();
    const newSchedule: AggregatedSchedule = {
      days: missingDays,
      startTime: defaultConfig.startTime,
      endTime: defaultConfig.endTime,
      minimumMeetingLength: defaultConfig.minimumMeetingLength,
      maximumMeetingLength: defaultConfig.maximumMeetingLength,
      defaultMeetingLength: defaultConfig.defaultMeetingLength,
      minutesBetweenMeetings: defaultConfig.minutesBetweenMeetings,
      slots: [
        {
          id: uuidv4(),
          days: missingDays,
          defaultMeetingLength: defaultConfig.defaultMeetingLength,
          endTime: defaultConfig.endTime,
          maximumMeetingLength: defaultConfig.maximumMeetingLength,
          minimumMeetingLength: defaultConfig.minimumMeetingLength,
          minutesBetweenMeetings: defaultConfig.minutesBetweenMeetings,
          startTime: defaultConfig.startTime,
        },
      ],
    };

    setAggregatedSchedules((prev) => [...prev, newSchedule]);
  };

  const removeScheduleGroup = (schedIndex: number) => {
    setAggregatedSchedules((prev) => prev.filter((_, i) => i !== schedIndex));
  };

  const shouldShowAddButton = () => {
    const allDays = [0, 1, 2, 3, 4, 5, 6];
    const existingDays = aggregatedSchedules.reduce(
      (days: number[], sched) => [...days, ...sched.days],
      []
    );
    return new Set(existingDays).size < allDays.length;
  };

  // ── Save ────────────────────────────────────────────────

  const handleSave = async () => {
    const validationError = validateSchedules(aggregatedSchedules);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setIsSaving(true);
    try {
      const scheduleForSubmission: any = {};

      for (const schedule of aggregatedSchedules) {
        const { days, slots } = schedule;
        for (const day of days) {
          if (!scheduleForSubmission[day]) {
            scheduleForSubmission[day] = { slots: [] };
          }
          for (const slot of slots) {
            const existingSlot = scheduleForSubmission[day].slots.find(
              (s: any) =>
                s.startTime === slot.startTime &&
                s.endTime === slot.endTime &&
                s.defaultMeetingLength === slot.defaultMeetingLength
            );
            if (!existingSlot) {
              scheduleForSubmission[day].slots.push({
                days: [day],
                startTime: slot.startTime,
                endTime: slot.endTime,
                minimumMeetingLength: slot.minimumMeetingLength,
                maximumMeetingLength: slot.maximumMeetingLength,
                defaultMeetingLength: slot.defaultMeetingLength,
                minutesBetweenMeetings: slot.minutesBetweenMeetings,
              });
            } else {
              existingSlot.days.push(day);
            }
          }
        }
      }

      const payload = { ...scheduleForSubmission, userID };

      await API.graphql({
        query: maintainPersonDefaultSchedule,
        variables: { request: JSON.stringify(payload) },
      });

      toast.success('Schedule saved');
      onClose();
    } catch (error) {
      console.error('Error saving schedule:', error);
      toast.error('There was a problem saving the schedule');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Render ──────────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Working hours</DialogTitle>
          <DialogDescription className="sr-only">
            Configure your weekly working hours and availability
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 -mx-6 px-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="size-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-6">
              {aggregatedSchedules.map((sched, schedIndex) => (
                <div
                  key={`${sched.days.join(',')}-${schedIndex}`}
                  className="pb-6 border-b border-border last:border-b-0"
                >
                  {schedIndex > 0 && (
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-semibold text-foreground">
                        Additional days
                      </p>
                      <button
                        type="button"
                        onClick={() => removeScheduleGroup(schedIndex)}
                        className="text-sm font-medium text-destructive hover:text-destructive/80 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  )}

                  {schedIndex === 0 && (
                    <p className="text-sm font-semibold text-foreground mb-3">
                      Contact Information
                    </p>
                  )}

                  <DaySelector
                    selectedDays={sched.days}
                    onToggle={(days) => updateScheduleDays(schedIndex, days)}
                  />

                  {sched.slots.map((slot) => (
                    <TimeSlotRow
                      key={slot.id}
                      slot={slot}
                      onChangeStart={(val) =>
                        updateSlotStartTime(schedIndex, slot.id!, val)
                      }
                      onChangeEnd={(val) =>
                        updateSlotEndTime(schedIndex, slot.id!, val)
                      }
                      onChangeDuration={(val) =>
                        updateSlotDuration(schedIndex, slot.id!, val)
                      }
                      onAdd={() => addSlot(schedIndex)}
                      onRemove={() => removeSlot(slot.id!)}
                    />
                  ))}
                </div>
              ))}

              {shouldShowAddButton() && (
                <button
                  type="button"
                  onClick={addAnotherDay}
                  className="flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                >
                  <Plus className="size-4" />
                  Add another day
                </button>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="pt-4 border-t border-border gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSaving}
            className="rounded-full px-6"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || isLoading}
            className="rounded-full px-6 bg-[hsl(var(--primary-dark))] hover:bg-[hsl(var(--primary-dark))]/90 text-white"
          >
            {isSaving && <Loader2 className="size-4 animate-spin mr-2" />}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
